/**
 * Replies page — lists inbound email replies classified by /api/reply-intel,
 * shows intent + confidence + matched lead + auto-moved status, and lets
 * the operator generate 3 AI reply drafts in 1 click.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox, Sparkles, Mail, Clock, CheckCircle2, Loader2, Filter,
  TrendingUp, Ban, Calendar, ArrowRight, AlertTriangle,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import ReplyDraftsModal from "../components/ReplyDraftsModal";
import { useLang } from "../i18n/LangContext";
import { useAutoRefresh, AutoRefreshToggle } from "../hooks/useAutoRefresh";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_STYLE = {
  positive:        { bg: "bg-emerald-100",  text: "text-emerald-700",  icon: TrendingUp },
  booked_meeting:  { bg: "bg-blue-100",     text: "text-blue-700",     icon: Calendar },
  objection:       { bg: "bg-amber-100",    text: "text-amber-700",    icon: AlertTriangle },
  not_interested:  { bg: "bg-rose-100",     text: "text-rose-700",     icon: Ban },
  unsubscribe:     { bg: "bg-gray-200",     text: "text-gray-700",     icon: Ban },
  out_of_office:   { bg: "bg-indigo-100",   text: "text-indigo-700",   icon: Clock },
  unclear:         { bg: "bg-gray-100",     text: "text-gray-600",     icon: Mail },
};

export default function Replies() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [stats, setStats] = useState({ total_replies: 0, auto_moved: 0, by_intent: [] });
  const [replies, setReplies] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/reply-intel/stats`),
        axios.get(`${API}/reply-intel/replies`, { params: { limit: 80, intent: filter || undefined } }),
      ]);
      setStats(s.data);
      setReplies(l.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);
  const auto = useAutoRefresh(reload, 30000, { defaultEnabled: true });

  const byIntentMap = Object.fromEntries((stats.by_intent || []).map((x) => [x.intent, x.count]));

  return (
    <div className="min-h-screen" style={{ background: "var(--al-bg)" }} data-testid="replies-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col">
        <header className="flex items-center gap-3 px-4 lg:px-8 h-[70px] border-b border-[var(--al-border)] bg-[var(--al-card-bg)]">
          <Logo size={28} />
          <div className="w-9 h-9 rounded-lg bg-[#8B5CF6] text-white flex items-center justify-center">
            <Inbox className="w-5 h-5" />
          </div>
          <h1 className="text-[16px] font-bold tracking-tight">
            <span className="text-[#8B5CF6]">{isPt ? "Respostas IA" : "AI Replies"}</span>
            <span className="text-[var(--al-text-muted)] ml-1.5 font-normal">— {isPt ? "caixa classificada" : "classified inbox"}</span>
          </h1>
          <span className="ml-3 text-[10px] font-bold px-2 py-0.5 rounded bg-violet-100 text-violet-700 uppercase">
            GPT
          </span>
          <AutoRefreshToggle {...auto} label={isPt ? "Auto-atualizar" : "Auto-refresh"} />
          <div className="ml-auto flex items-center gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-6 lg:px-10 py-6 max-w-[1320px] w-full">
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi testId="rep-kpi-total" label={isPt ? "Respostas totais" : "Total replies"} value={stats.total_replies} tone="text-[#8B5CF6]" />
            <Kpi testId="rep-kpi-auto" label={isPt ? "Auto-movidas no Kanban" : "Auto-moved in Kanban"} value={stats.auto_moved} tone="text-emerald-600" />
            <Kpi testId="rep-kpi-positive" label={isPt ? "Positivas" : "Positive"} value={byIntentMap.positive || 0} tone="text-emerald-600" />
            <Kpi testId="rep-kpi-objection" label={isPt ? "Objeções" : "Objections"} value={byIntentMap.objection || 0} tone="text-amber-600" />
          </div>

          {/* Intent filter pills */}
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="rep-filter-bar">
            <button type="button" onClick={() => setFilter("")}
              data-testid="rep-filter-all"
              className={
                "px-2.5 h-7 rounded-full text-[11px] font-bold border " +
                (filter === "" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#8B5CF6]")
              }>{isPt ? "Todas" : "All"}</button>
            {Object.keys(INTENT_STYLE).map((k) => {
              const cnt = byIntentMap[k] || 0;
              if (cnt === 0 && filter !== k) return null;
              const active = filter === k;
              const style = INTENT_STYLE[k];
              return (
                <button key={k} type="button" onClick={() => setFilter(active ? "" : k)}
                  data-testid={`rep-filter-${k}`}
                  className={
                    "px-2.5 h-7 rounded-full text-[11px] font-bold border transition-colors " +
                    (active
                      ? "bg-[#8B5CF6] text-white border-[#8B5CF6]"
                      : `${style.bg} ${style.text} border-transparent hover:opacity-90`)
                  }>
                  {k} <span className="opacity-70 tabular-nums">· {cnt}</span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="al-card p-10 text-center text-[var(--al-text-muted)]">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              {isPt ? "Carregando..." : "Loading..."}
            </div>
          ) : replies.length === 0 ? (
            <div className="al-card p-10 text-center text-[var(--al-text-muted)] text-[13px]">
              {isPt
                ? "Ainda sem respostas. Configure SendGrid/Mailgun Inbound Parse para apontar pra /api/reply-intel/webhook/reply."
                : "No replies yet. Wire SendGrid/Mailgun Inbound Parse to /api/reply-intel/webhook/reply."}
            </div>
          ) : (
            <div className="space-y-2" data-testid="rep-list">
              {replies.map((r) => {
                const style = INTENT_STYLE[r.intent] || INTENT_STYLE.unclear;
                const IntentIcon = style.icon;
                const draftable = !["unsubscribe", "out_of_office"].includes(r.intent);
                return (
                  <div
                    key={r.id}
                    data-testid={`rep-row-${r.id}`}
                    className="al-card p-3 flex flex-col sm:flex-row gap-3 sm:items-center hover:border-[#8B5CF6] transition-colors"
                  >
                    <div className={`w-9 h-9 rounded-lg ${style.bg} ${style.text} flex items-center justify-center shrink-0`}>
                      <IntentIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-bold text-[13px] text-[var(--al-text)] truncate">{r.from_email}</span>
                        <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded uppercase ${style.bg} ${style.text}`}>{r.intent}</span>
                        <span className="text-[10px] text-[var(--al-text-muted)]">{r.confidence}%</span>
                        {r.auto_moved_to && (
                          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white inline-flex items-center gap-0.5">
                            <CheckCircle2 className="w-2.5 h-2.5" /> → {r.auto_moved_to}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-[var(--al-text)] truncate">{r.subject || <span className="italic text-[var(--al-text-muted)]">({isPt ? "sem assunto" : "no subject"})</span>}</div>
                      <div className="text-[11px] text-[var(--al-text-muted)] line-clamp-1 mt-0.5">{r.body_preview}</div>
                    </div>
                    {draftable && (
                      <button type="button" onClick={() => setActive(r)}
                        data-testid={`rep-draft-${r.id}`}
                        className="h-8 px-3 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 shrink-0">
                        <Sparkles className="w-3.5 h-3.5" />
                        {isPt ? "Rascunhar IA" : "AI Draft"}
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      <ReplyDraftsModal
        open={!!active}
        reply={active}
        onClose={() => setActive(null)}
        onSent={reload}
      />
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  return (
    <div className="al-card p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">{label}</div>
      <div className={`text-[26px] font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
